// Florals / ThemeDeco — the decorative motif for the current Keep theme. Legacy Keeps (motif
// "floral") render the real watercolor bougainvillea border; the eight themes each render their
// own hand-drawn line motif (rule / deco / arch / confetti / dots / sprig-star / horizon) in the
// theme's accent. Keeps the original FloralSpray API (variant/density/className) so screens need
// no changes; the accent is read from the CSS var so it themes automatically.
import type { CSSProperties, ReactNode } from "react";
import { useKeepTheme } from "../lib/keep";
import type { ThemeMotif } from "@wedding/shared";

export type FloralVariant = "topBorder" | "cornerTL" | "cornerTR" | "sprig";
export type FloralDensity = "none" | "subtle" | "lush";

const AC = "var(--c-accent)"; // motifs draw in the theme accent (auto-themes)
const LN = "var(--c-ink-soft)";

const SRC: Record<FloralVariant, string> = {
  topBorder: "/florals/border-top.webp",
  cornerTL: "/florals/corner-tl.webp",
  cornerTR: "/florals/corner-tr.webp",
  sprig: "/florals/sprig.webp",
};

const OBJECT_POSITION: Record<FloralVariant, string> = {
  topBorder: "center top",
  cornerTL: "left top",
  cornerTR: "right top",
  sprig: "center top",
};

interface FloralSprayProps {
  variant?: FloralVariant;
  density?: FloralDensity;
  width?: number; // accepted for API compatibility (unused)
  height?: number; // accepted for API compatibility (unused)
  seed?: number; // accepted for API compatibility (unused)
  style?: CSSProperties;
  className?: string;
}

export function FloralSpray({ variant = "topBorder", density = "lush", style, className = "" }: FloralSprayProps) {
  const { motif } = useKeepTheme();
  if (density === "none") return null;
  if (motif === "floral") {
    return (
      <img
        src={SRC[variant]}
        className={"floral-img " + className}
        alt=""
        aria-hidden="true"
        style={{ objectPosition: OBJECT_POSITION[variant], opacity: density === "subtle" ? 0.5 : undefined, ...style }}
      />
    );
  }
  const inner = motifSvg(motif, variant);
  if (!inner) return null;
  // cornerTR mirrors the top-left corner motif.
  const flip = variant === "cornerTR" ? { transform: "scaleX(-1)" } : null;
  return (
    <span className={"themedeco " + className} style={{ display: "block", opacity: density === "subtle" ? 0.6 : undefined, ...flip, ...style }} aria-hidden="true">
      {inner}
    </span>
  );
}

// ── theme line motifs (ported from the design's ThemeDeco) ──
type MV = "topBorder" | "cornerTL" | "cornerTR" | "sprig";
const svgProps = (vb: string): { viewBox: string; preserveAspectRatio: string; width: string; height: string } => ({
  viewBox: vb, preserveAspectRatio: "xMidYMid meet", width: "100%", height: "100%",
});

function sunRays(cx: number, cy: number, count = 7, r0 = 18, r1 = 25): ReactNode[] {
  const rays: ReactNode[] = [];
  for (let i = 0; i <= count; i++) {
    const a = Math.PI * (i / count);
    rays.push(<line key={i} x1={cx - Math.cos(a) * r0} y1={cy - Math.sin(a) * r0} x2={cx - Math.cos(a) * r1} y2={cy - Math.sin(a) * r1} stroke={AC} strokeWidth="1.2" strokeLinecap="round" opacity={0.55 + 0.45 * (1 - Math.abs(i - count / 2) / (count / 2))} />);
  }
  return rays;
}

function motifSvg(motif: ThemeMotif, variant: MV): ReactNode {
  const corner = variant === "cornerTL" || variant === "cornerTR";
  if (motif === "rule") {
    // Minimal hairline — top border only; no corner/sprig decoration.
    if (variant !== "topBorder") return null;
    return (
      <svg {...svgProps("0 0 200 60")} fill="none" strokeLinecap="round">
        <line x1="40" y1="36" x2="92" y2="36" stroke={LN} strokeWidth="1" opacity="0.6" />
        <line x1="108" y1="36" x2="160" y2="36" stroke={LN} strokeWidth="1" opacity="0.6" />
        <circle cx="100" cy="36" r="4" fill="none" stroke={AC} strokeWidth="1.4" />
        <circle cx="100" cy="36" r="1.2" fill={AC} />
      </svg>
    );
  }
  if (motif === "deco") {
    if (variant === "topBorder") return (
      <svg {...svgProps("0 0 200 96")} fill="none" stroke={AC} strokeLinecap="round">
        <g strokeWidth="1.3"><line x1="14" y1="40" x2="78" y2="40" /><line x1="122" y1="40" x2="186" y2="40" /><line x1="20" y1="48" x2="72" y2="48" opacity="0.5" /><line x1="128" y1="48" x2="180" y2="48" opacity="0.5" /></g>
        {[-40, -26, -13, 0, 13, 26, 40].map((d, i) => <line key={i} x1="100" y1="44" x2={100 + Math.sin(d * Math.PI / 180) * 34} y2={44 - Math.cos(d * Math.PI / 180) * 30} strokeWidth="1.2" opacity={0.55 + 0.45 * (1 - Math.abs(d) / 40)} />)}
        <path d="M100 47 l7 -8 -7 -4 -7 4 Z" fill={AC} stroke="none" />
        <circle cx="78" cy="40" r="2.4" fill={AC} stroke="none" /><circle cx="122" cy="40" r="2.4" fill={AC} stroke="none" />
      </svg>
    );
    if (corner) return (
      <svg {...svgProps("0 0 100 100")} fill="none" stroke={AC} strokeLinecap="round">
        <path d="M12 44 V12 H44" strokeWidth="1.4" /><path d="M20 40 V20 H40" strokeWidth="1.1" opacity="0.6" />
        {[20, 38, 56, 74].map((a, i) => <line key={i} x1="16" y1="16" x2={16 + Math.cos(a * Math.PI / 180) * 30} y2={16 + Math.sin(a * Math.PI / 180) * 30} strokeWidth="1.1" opacity="0.7" />)}
        <circle cx="16" cy="16" r="2.6" fill={AC} stroke="none" />
      </svg>
    );
    return (
      <svg {...svgProps("0 0 80 120")} fill="none" stroke={AC} strokeLinecap="round">
        <line x1="40" y1="14" x2="40" y2="106" strokeWidth="1.1" opacity="0.5" />
        {[26, 50, 74].map((y, i) => <g key={i}><path d={`M40 ${y - 9} l9 9 -9 9 -9 -9 Z`} strokeWidth="1.2" /><circle cx="40" cy={y} r="1.6" fill={AC} stroke="none" /></g>)}
      </svg>
    );
  }
  if (motif === "arch") {
    if (variant === "topBorder") {
      const rays: ReactNode[] = [];
      for (let i = 0; i <= 10; i++) { const a = (i / 10) * Math.PI; rays.push(<line key={i} x1={100 - Math.cos(a) * 40} y1={70 - Math.sin(a) * 40} x2={100 - Math.cos(a) * 48} y2={70 - Math.sin(a) * 48} stroke={AC} strokeWidth="1.6" strokeLinecap="round" />); }
      return (
        <svg {...svgProps("0 0 200 78")}>
          <path d="M64 72 A36 36 0 0 1 136 72" fill={AC} fillOpacity="0.12" stroke={AC} strokeWidth="2" />
          <path d="M74 72 A26 26 0 0 1 126 72" fill="none" stroke={AC} strokeWidth="1.2" opacity="0.5" />
          {rays}<circle cx="44" cy="70" r="2.6" fill={AC} /><circle cx="156" cy="70" r="2.6" fill={AC} />
        </svg>
      );
    }
    if (corner) {
      const rays: ReactNode[] = [];
      for (let i = 0; i <= 6; i++) { const a = (i / 6) * (Math.PI / 2); rays.push(<line key={i} x1={Math.cos(a) * 30} y1={Math.sin(a) * 30} x2={Math.cos(a) * 40} y2={Math.sin(a) * 40} stroke={AC} strokeWidth="1.5" strokeLinecap="round" />); }
      return <svg {...svgProps("0 0 100 100")}><g transform="translate(14 14)"><path d="M0 30 A30 30 0 0 1 30 0" fill="none" stroke={AC} strokeWidth="1.6" />{rays}</g></svg>;
    }
    return (
      <svg {...svgProps("0 0 80 120")}>
        {[28, 60, 92].map((y, i) => {
          const rays: ReactNode[] = [];
          for (let k = 0; k <= 6; k++) { const a = (k / 6) * Math.PI; rays.push(<line key={k} x1={40 - Math.cos(a) * 12} y1={y - Math.sin(a) * 12} x2={40 - Math.cos(a) * 16} y2={y - Math.sin(a) * 16} stroke={AC} strokeWidth="1.3" strokeLinecap="round" />); }
          return <g key={i}><path d={`M${40 - 11} ${y} A11 11 0 0 1 ${40 + 11} ${y}`} fill="none" stroke={AC} strokeWidth="1.3" />{rays}</g>;
        })}
      </svg>
    );
  }
  if (motif === "confetti") {
    const bits = (arr: [number, number, string, number?][]): ReactNode =>
      arr.map(([x, y, t, r = 0], i) => t === "c" ? <circle key={i} cx={x} cy={y} r="2" fill={AC} stroke="none" />
        : t === "o" ? <circle key={i} cx={x} cy={y} r="2.3" fill="none" stroke={AC} strokeWidth="1.2" />
        : t === "t" ? <path key={i} d={`M${x} ${y - 3.4} l3.4 5.6 -6.8 0 Z`} fill="none" stroke={AC} strokeWidth="1.2" transform={`rotate(${r} ${x} ${y})`} />
        : <line key={i} x1={x - 3} y1={y - 3} x2={x + 3} y2={y + 3} stroke={AC} strokeWidth="1.5" transform={`rotate(${r} ${x} ${y})`} />);
    if (variant === "topBorder") return <svg {...svgProps("0 0 200 74")} strokeLinecap="round">{bits([[22, 44, "l", 20], [40, 28, "c"], [58, 50, "t", 14], [76, 26, "l", -30], [94, 46, "o"], [100, 60, "c"], [112, 30, "t", -18], [128, 52, "l", 40], [146, 28, "c"], [162, 48, "o"], [180, 34, "t", 25]])}</svg>;
    if (corner) return <svg {...svgProps("0 0 100 100")} strokeLinecap="round">{bits([[16, 16, "o"], [34, 12, "t", 20], [12, 34, "l", -20], [30, 32, "c"], [48, 20, "l", 40], [22, 50, "t", -15], [46, 44, "c"]])}</svg>;
    return <svg {...svgProps("0 0 80 120")} strokeLinecap="round">{bits([[40, 20, "c"], [30, 34, "t", 18], [50, 40, "l", -25], [38, 54, "o"], [48, 68, "c"], [32, 78, "l", 30], [42, 92, "t", -12], [50, 104, "c"]])}</svg>;
  }
  if (motif === "dots") {
    if (variant === "topBorder") return (
      <svg {...svgProps("0 0 200 74")} fill="none" stroke={AC} strokeLinecap="round">
        <path d="M30 30 Q100 64 170 30" strokeWidth="1.2" />
        {([[30, 30], [65, 43], [100, 47], [135, 43], [170, 30]] as [number, number][]).map(([x, y], i) => <g key={i}><line x1={x} y1={y} x2={x} y2={y + 8} strokeWidth="1" opacity="0.7" /><circle cx={x} cy={y + 11} r="2.4" fill={AC} stroke="none" /></g>)}
      </svg>
    );
    if (corner) return (
      <svg {...svgProps("0 0 100 100")} fill="none" stroke={AC} strokeLinecap="round">
        <path d="M14 46 Q16 16 46 14" strokeWidth="1.2" />
        {([[22, 30], [30, 22]] as [number, number][]).map(([x, y], i) => <g key={i}><line x1={x} y1={y} x2={x + 5} y2={y + 5} strokeWidth="1" opacity="0.7" /><circle cx={x + 7} cy={y + 7} r="2.3" fill={AC} stroke="none" /></g>)}
      </svg>
    );
    return (
      <svg {...svgProps("0 0 80 120")} fill="none" stroke={AC} strokeLinecap="round">
        <line x1="40" y1="14" x2="40" y2="106" strokeWidth="1.1" opacity="0.55" />
        {[30, 56, 82].map((y, i) => <g key={i}><line x1="40" y1={y} x2="40" y2={y + 7} strokeWidth="1" /><circle cx="40" cy={y + 10} r="2.6" fill={AC} stroke="none" /></g>)}
      </svg>
    );
  }
  if (motif === "sprigstar") {
    const star = (cx: number, cy: number, r: number): ReactNode => (
      <g>{[0, 45, 90, 135].map((a, i) => { const rad = a * Math.PI / 180, rr = a % 90 === 0 ? r : r * 0.58; return <line key={i} x1={cx - Math.cos(rad) * rr} y1={cy - Math.sin(rad) * rr} x2={cx + Math.cos(rad) * rr} y2={cy + Math.sin(rad) * rr} stroke={AC} strokeWidth="1.2" strokeLinecap="round" />; })}</g>
    );
    const pine = (x1: number, y: number, dir: number): ReactNode => {
      const needles: ReactNode[] = [];
      for (let i = 1; i <= 4; i++) { const px = x1 + dir * i * 8; needles.push(<line key={"a" + i} x1={px} y1={y} x2={px - dir * 5} y2={y - 6} stroke={AC} strokeWidth="1.1" strokeLinecap="round" />); needles.push(<line key={"b" + i} x1={px} y1={y} x2={px - dir * 5} y2={y + 6} stroke={AC} strokeWidth="1.1" strokeLinecap="round" />); }
      return <g><line x1={x1} y1={y} x2={x1 + dir * 34} y2={y} stroke={AC} strokeWidth="1.2" strokeLinecap="round" />{needles}</g>;
    };
    if (variant === "topBorder") return <svg {...svgProps("0 0 200 74")}>{star(100, 38, 15)}{pine(78, 38, -1)}{pine(122, 38, 1)}</svg>;
    if (corner) return <svg {...svgProps("0 0 100 100")}>{star(20, 20, 10)}<g transform="rotate(45 40 40)">{pine(30, 40, 1)}</g></svg>;
    const needles: ReactNode[] = [];
    [30, 52, 74, 96].forEach((y, i) => { needles.push(<line key={"a" + i} x1="40" y1={y} x2="30" y2={y + 8} stroke={AC} strokeWidth="1.1" strokeLinecap="round" />); needles.push(<line key={"b" + i} x1="40" y1={y} x2="50" y2={y + 8} stroke={AC} strokeWidth="1.1" strokeLinecap="round" />); });
    return <svg {...svgProps("0 0 80 120")}>{star(40, 16, 9)}<line x1="40" y1="24" x2="40" y2="104" stroke={AC} strokeWidth="1.1" strokeLinecap="round" opacity="0.6" />{needles}</svg>;
  }
  if (motif === "horizon") {
    if (variant === "topBorder") return (
      <svg {...svgProps("0 0 200 74")} fill="none" stroke={AC} strokeLinecap="round">
        <line x1="26" y1="54" x2="82" y2="54" strokeWidth="1.2" /><line x1="118" y1="54" x2="174" y2="54" strokeWidth="1.2" />
        <line x1="34" y1="60" x2="70" y2="60" strokeWidth="1" opacity="0.5" /><line x1="130" y1="60" x2="166" y2="60" strokeWidth="1" opacity="0.5" />
        <path d="M84 54 A16 16 0 0 1 116 54" strokeWidth="1.5" />{sunRays(100, 54)}
      </svg>
    );
    if (corner) return (
      <svg {...svgProps("0 0 100 100")} fill="none" stroke={AC} strokeLinecap="round">
        <g transform="rotate(-45 24 24)"><path d="M10 24 A14 14 0 0 1 38 24" strokeWidth="1.4" />{sunRays(24, 24, 5, 16, 23)}</g>
      </svg>
    );
    return (
      <svg {...svgProps("0 0 80 120")} fill="none" stroke={AC} strokeLinecap="round">
        <circle cx="40" cy="26" r="12" strokeWidth="1.3" />{sunRays(40, 26, 8, 15, 21)}
        {[54, 72, 90].map((y, i) => <path key={i} d={`M26 ${y} q7 -6 14 0 t14 0`} strokeWidth="1.1" opacity="0.6" />)}
      </svg>
    );
  }
  return null;
}

// small engraved ornament divider (unchanged — this is line art, not floral)
export function Ornament({ color = "currentColor", width = 120 }: { color?: string; width?: number }) {
  return (
    <svg width={width} height="14" viewBox="0 0 120 14" style={{ display: "block" }} aria-hidden="true">
      <line x1="6" y1="7" x2="50" y2="7" stroke={color} strokeWidth="1" />
      <line x1="70" y1="7" x2="114" y2="7" stroke={color} strokeWidth="1" />
      <circle cx="60" cy="7" r="3.4" fill="none" stroke={color} strokeWidth="1" />
      <circle cx="60" cy="7" r="1.1" fill={color} />
      <circle cx="52.5" cy="7" r="1" fill={color} />
      <circle cx="67.5" cy="7" r="1" fill={color} />
    </svg>
  );
}
